import React, { useContext, useEffect, useMemo, useState } from 'react';

import {
  convertStringsToDates,
  FilterType,
  getPageSizeFromQueryString,
  NativeFilterTypes,
  QueryString,
  Table,
} from 'design-system-web';

import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { useLazyQuery, useQuery } from '@apollo/client';

import { getClient } from 'lane-shared/apollo';
import { routes } from 'lane-shared/config';
import { UserDataContext } from 'lane-shared/contexts';
import {
  AdminUser,
  useAssignableMembers,
} from 'lane-shared/domains/workOrder/hooks/useAssignableMembers';
import { queryChannels } from 'lane-shared/graphql/channel';
import { convertTo62, convertToUUID, safeConvertToUUID } from 'uuid-encoding';
import { useFlag } from 'lane-shared/hooks';
import { FeatureFlag } from 'constants-flags';

import { WORK_ORDER_SCHEDULE_TABLE_STATE } from 'helpers/constants/localstorageKey';
import { hasPermission } from 'lane-shared/helpers';
import { PERMISSION_KEYS } from 'constants-permissions';
import { usePersistedParams, useQueryString } from 'hooks';

import { getAllEquipmentQuery, searchPMSchedulesQuery } from 'graphql-queries';

import {
  ClientPmSchedule,
  Equipment,
  IdNamePair,
  SortDirection,
  StringSearchType,
} from 'graphql-query-contracts';

import { DangerousTranslate } from 'components/DangerousTranslate';
import { EmptyPageView } from 'components/layout';
import styles from './styles.scss';
import { exportCSV } from '../../../../helpers/exportCSV';
import { Channel } from 'packages/lane-shared/types/ChannelType';
import { SortDirectionEnum } from 'constants-activate';

const PER_PAGE = 25;
const TOASTER_SECONDS = 5000;

const DEFAULT_SEARCH_PARAMS = {
  // table params
  page: 0,
  pageSize: PER_PAGE,
  total: 0,
  sortBy: 'created_at',
  sortDirection: SortDirection.Desc,
  // filters
  assignee: '',
  assignee_groups: '',
  equipment: '',
  keyword: '',
  location: '',
  next_due_date: '',
  recurrence: '',
};

type ScheduleQueryString = {
  equipment: string;
  tab: string;
  sortDirection: SortDirectionEnum;
} & QueryString;

const normalizeSortDirection = (
  direction: SortDirectionEnum
): SortDirection => {
  switch (direction) {
    case SortDirectionEnum.Asc:
      return SortDirection.Asc;
    case SortDirectionEnum.Desc:
      return SortDirection.Desc;
    default:
      return SortDirection.Desc;
  }
};

function ScheduleList({ channel }: any) {
  const { t } = useTranslation();
  const dataImporterFlag = useFlag(FeatureFlag.DataImporter, false);

  const storedTableState = window.localStorage.getItem(
    WORK_ORDER_SCHEDULE_TABLE_STATE
  );
  const initialTableParams = storedTableState
    ? JSON.parse(storedTableState)
    : DEFAULT_SEARCH_PARAMS;
  const [searchParams, setSearchParams] =
    useQueryString<ScheduleQueryString>(initialTableParams);

  const { user } = useContext(UserDataContext);

  const canCreateSchedule =
    user?.isSuperUser ||
    hasPermission(
      user?.roles,
      [PERMISSION_KEYS.PERMISSION_WORK_ORDERS_PM_SCHEDULE_CREATE],
      channel?._id,
      false
    );

  const [equipments, setEquipments] = useState<Equipment[]>([]);
  const [exportLoading, setExportLoading] = useState<boolean>(false);

  const isCrossProperty = channel?.settings?.hasWorkOrderCrossPropertyEnabled;

  const subChannelsResult = useQuery(queryChannels, {
    variables: {
      pagination: {
        start: 0,
        perPage: 100,
      },
      search: {
        sortBy: {
          key: 'name',
          dir: SortDirection.Asc,
        },
        // isSub: true,
        parent: {
          _id: channel?._id,
        },
      },
    },
  });

  let subChannelIds: string[] = [];

  if (isCrossProperty) {
    subChannelIds =
      subChannelsResult?.data?.channels?.items?.map(
        (subChannel: Channel) => subChannel?._id
      ) || [];
  }

  const pageSize = getPageSizeFromQueryString(searchParams?.pageSize, PER_PAGE);

  const queryVariables = {
    refIds: channel?._id ? [channel?._id, ...subChannelIds] : [],
    search: {
      ...(searchParams.sortBy
        ? {
            sortBy: {
              key: searchParams.sortBy,
              dir: normalizeSortDirection(searchParams.sortDirection),
            },
          }
        : {}),
      ...(searchParams.keyword
        ? {
            search: {
              type: StringSearchType.Like,
              value: searchParams.keyword,
            },
          }
        : {}),
    },
    pagination: {
      start: ((searchParams?.page || 0) as number) * pageSize,
      perPage: pageSize,
    },
    filter: {
      equipments: searchParams?.equipment
        ? searchParams?.equipment?.split(',')
        : [],
      ...(searchParams?.assignee
        ? {
            assignees: searchParams?.assignee.split(',').map(safeConvertToUUID),
          }
        : {}),
      ...(searchParams?.assignee_groups
        ? {
            assigneeGroups: searchParams?.assignee_groups
              .split(',')
              .map(safeConvertToUUID),
          }
        : {}),
      ...(searchParams?.location
        ? {
            locations: searchParams?.location.split(','),
          }
        : {}),
      ...(searchParams?.recurrence
        ? {
            recurrences: searchParams?.recurrence.split(','),
          }
        : {}),
      ...(searchParams?.next_due_date
        ? {
            nextDueDateStart: convertStringsToDates(searchParams?.next_due_date)
              ?.startDate,
            nextDueDateEnd: convertStringsToDates(searchParams?.next_due_date)
              ?.endDate,
          }
        : {}),
    },
    includeArchived: false,
  };

  const { data, loading: schedulesLoading } = useQuery(searchPMSchedulesQuery, {
    variables: queryVariables,
    fetchPolicy: 'network-only',
  });

  const totalSchedules = data?.findSchedules?.pageInfo?.total;

  const [_adminUser, __, assignableTeams] = useAssignableMembers(channel?._id);
  const [filterData, setFilterData] = useState<any>();

  const teams = assignableTeams?.map((s: AdminUser) => ({
    label: s.label,
    value: convertTo62(s.value),
  }));

  useEffect(() => {
    if (totalSchedules) {
      setSearchParams({ total: totalSchedules });
    }
  }, [data?.findSchedules?.pageInfo]);

  usePersistedParams({
    searchParams,
    initialTableParams,
    tableStorageVariable: WORK_ORDER_SCHEDULE_TABLE_STATE,
  });

  useEffect(() => {
    setSearchParams(initialTableParams);
  }, [searchParams?.tab]);

  const [fetchEquipment, { data: equipmentData, loading }] = useLazyQuery(
    getAllEquipmentQuery,
    {
      onCompleted: () => {
        if (equipmentData?.getAllEquipment) {
          setEquipments(equipmentData.getAllEquipment);
        }
      },
    }
  );

  useEffect(() => {
    if (channel?._id) {
      fetchEquipment({
        variables: {
          channelId: channel._id,
          includeArchived: false,
        },
      });
    }
  }, [channel?._id, fetchEquipment]);

  const equipmentMap = useMemo(() => {
    const equipmentMap = new Map<string, Equipment>();

    if (!data?.findSchedules?.schedules || equipments.length === 0)
      return equipmentMap;

    const { schedules } = data.findSchedules;

    for (let i = 0; i < schedules.length; i++) {
      const { equipmentIds } = schedules[i];

      if (!equipmentIds || equipmentIds.length === 0) {
        continue;
      }

      equipmentIds.forEach((eqId: string) => {
        if (!eqId || equipmentMap.has(eqId)) return;

        const equipment = equipments.find((e: Equipment) => e.id === eqId);

        if (!equipment) return;

        equipmentMap.set(eqId, equipment);
      });
    }

    return equipmentMap;
  }, [data?.findSchedules?.schedules, equipments]);

  let columns = [
    {
      header: t`web.admin.workOrder.preventiveMaintenance.schedule.tableColumn.title`,
      key: 'title',
      disableVisibilityToggle: true,
      renderCell: (title: string, row: ClientPmSchedule) => (
        <Link
          to={routes.channelAdminWorkOrdersPMScheduleDetails
            .replace(':id', channel?.slug)
            .replace(':scheduleId', row.scheduleId ?? '')}
        >
          {title}
        </Link>
      ),
      renderForCSV: (title: string) => title,
    },
    {
      header: t`web.admin.workOrder.preventiveMaintenance.schedule.tableColumn.equipment`,
      key: 'equipmentIds',
      disableSorting: true,
      renderCell: (equipmentIds: string[]) => {
        const equipmentsOnSchedule: string[] = [];

        equipmentIds.forEach((eqId: string) => {
          const equipment = equipmentMap.get(eqId);

          if (!equipment) return;

          equipmentsOnSchedule.push(equipment.name);
        });

        return equipmentsOnSchedule.join(', ');
      },
    },
    {
      header: t`web.admin.workOrder.preventiveMaintenance.schedule.tableColumn.location`,
      key: 'location',
      disableSorting: true,
      renderCell: (location: string, row: ClientPmSchedule) => {
        const equipmentLocations: string[] = [];

        row.equipmentIds?.forEach(eqId => {
          if (!eqId) return;

          const equipment = equipmentMap.get(eqId);

          if (!equipment || !equipment.location) return;

          equipmentLocations.push(equipment.location);
        });

        if (equipmentLocations.length === 0 && location) {
          return <div className={styles.locationCell}>{location}</div>;
        }

        return equipmentLocations.join(', ');
      },
      renderForCSV: (location: string) => location,
    },
    {
      header: t`web.admin.workOrder.preventiveMaintenance.schedule.tableColumn.floor`,
      key: 'floor',
      disableSorting: true,
      renderCell: (_: any, row: ClientPmSchedule) => {
        const equipmentFloors: string[] = [];

        row.equipmentIds?.forEach(eqId => {
          if (!eqId) return;

          const equipment = equipmentMap.get(eqId);

          if (!equipment || !equipment.floor) return;

          equipmentFloors.push(equipment.floor);
        });

        return equipmentFloors.join(', ');
      },
    },
    {
      header: t`web.admin.workOrder.preventiveMaintenance.schedule.tableColumn.suite`,
      key: 'suite',
      disableSorting: true,
      renderCell: (_: any, row: ClientPmSchedule) => {
        const equipmentSuites: string[] = [];

        row.equipmentIds?.forEach(eqId => {
          if (!eqId) return;

          const equipment = equipmentMap.get(eqId);

          if (!equipment || !equipment.suite) return;

          equipmentSuites.push(equipment.suite);
        });

        return equipmentSuites.join(', ');
      },
    },
    {
      header: t`web.admin.workOrder.preventiveMaintenance.schedule.tableColumn.assignee`,
      key: 'assignee',
      disableSorting: true,
      renderCell: (assignee: IdNamePair) => {
        return assignee ? assignee.name || '' : '';
      },
    },
    {
      header: t`web.admin.workOrder.preventiveMaintenance.schedule.tableColumn.assignedTeams`,
      key: 'assigneeGroups',
      disableSorting: true,
      renderCell: (teamIds: string[]) => {
        const assignedTeamNames: string[] = [];

        teamIds.forEach(teamId => {
          const t = assignableTeams.find(
            team => convertToUUID(team.value) === convertToUUID(teamId)
          );

          if (!t) return;

          assignedTeamNames.push(t?.label);
        });

        return assignedTeamNames.join(', ');
      },
    },
    {
      header: t`web.admin.workOrder.preventiveMaintenance.schedule.tableColumn.recurrence`,
      key: 'repeats',
      renderCell: (repeats: string) => {
        const repeatsKey = repeats.replace(/-/g, '');

        return t(
          `web.admin.workOrder.preventiveMaintenance.schedule.${repeatsKey}`
        );
      },
    },
    {
      header: t`web.admin.workOrder.preventiveMaintenance.schedule.tableColumn.nextDueDate`,
      type: 'date',
      key: 'next_due_date',
    },
  ];

  if (isCrossProperty) {
    columns.splice(1, 0, {
      key: 'channel',
      header: t('web.admin.serviceRequest.property'),
      type: 'text',
    });
  }

  if (!channel?.settings?.hasWorkOrderEquipmentEnabled) {
    columns = columns.filter(column => column.key !== 'equipmentIds');
  }

  const handleExportToCSV = async () => {
    setExportLoading(true);

    try {
      const { data } = await getClient().query({
        query: searchPMSchedulesQuery,
        variables: {
          ...queryVariables,
          forExport: true,
        },
        fetchPolicy: 'network-only',
      });

      const schedules = data?.findSchedules?.schedules?.map(
        (schedule: ClientPmSchedule) => ({
          ...schedule,
          equipment: schedule.equipmentIds,
          next_due_date: schedule.nextDueDate,
        })
      );

      setExportLoading(false);
      exportCSV(
        _.compact(schedules),
        columns,
        `preventative-maintenance-schedules-${new Date().toISOString()}.csv`
      );
      // FIXME: Log error for datadog, missing stack trace
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (error) {
      setExportLoading(false);
      window.Toast.show(
        t`web.admin.workOrder.table.export.error`,
        TOASTER_SECONDS
      );
    }
  };

  const exportOptions = [
    {
      label: t`web.admin.workOrder.preventiveMaintenance.schedule.exportCSV`,
      onClick: handleExportToCSV,
      loading: exportLoading,
    },
  ];

  const hasFilterValue =
    searchParams.equipment ||
    searchParams.keyword ||
    searchParams.assignee ||
    searchParams.assignee_groups ||
    searchParams.completed_at ||
    searchParams.due_date ||
    searchParams.equipment ||
    searchParams.location ||
    searchParams.next_due_date ||
    searchParams.recurrence ||
    searchParams.schedule ||
    searchParams.status;

  type FilterOption = {
    label: string;
    value: string;
  };

  const tableRows = useMemo(() => {
    if (!data?.findSchedules?.schedules) return [];

    const rows = data.findSchedules.schedules.map(
      (schedule: ClientPmSchedule) => ({
        ...schedule,
        equipment: schedule.equipmentIds,
        next_due_date: schedule.nextDueDate,
        channel: schedule.extRefId?.name,
      })
    );

    setFilterData(data?.findSchedules?.filterOptions);

    return rows;
  }, [data?.findSchedules?.schedules]);

  let filters: FilterType[] = [
    {
      key: 'equipment',
      label: t(
        'web.admin.workOrder.preventiveMaintenance.schedule.tableColumn.equipment'
      ),
      type: NativeFilterTypes.Multiselect,
      options:
        filterData?.equipments
          ?.map((eq: { name: string; id: string }) => ({
            label: eq.name,
            value: eq.id,
          }))
          .sort((a: FilterOption, b: FilterOption) =>
            a.label.localeCompare(b.label)
          ) || [],
    },
    {
      key: 'location',
      label: t(
        'web.admin.workOrder.preventiveMaintenance.schedule.tableColumn.location'
      ),
      type: NativeFilterTypes.Multiselect,
      options:
        filterData?.locations
          ?.map((loc: string) => loc.trim())
          .filter((loc: string) => loc !== '')
          .map((loc: string) => ({
            label: loc,
            value: loc,
          }))
          .sort((a: FilterOption, b: FilterOption) =>
            a.label.localeCompare(b.label)
          ) || [],
    },
    {
      key: 'assignee',
      label: t(
        'web.admin.workOrder.preventiveMaintenance.schedule.tableColumn.assignee'
      ),
      type: NativeFilterTypes.Multiselect,
      options: [
        {
          label: t('web.admin.serviceRequest.unassigned'),
          value: 'unassigned',
        },
        ...(filterData?.assignees
          ?.map((s: { name: string; _id: string }) => ({
            label: s.name,
            value: s._id,
          }))
          .sort((a: FilterOption, b: FilterOption) =>
            a.label.localeCompare(b.label)
          ) || []),
      ],
    },
    {
      key: 'assignee_groups',
      label: t('web.admin.serviceRequest.assigneeGroups'),
      type: NativeFilterTypes.Multiselect,
      options: [
        {
          label: t('web.admin.serviceRequest.unassigned'),
          value: 'unassigned',
        },
        ...teams
          .filter((team: FilterOption) => team.value !== 'unassigned')
          .sort((a: FilterOption, b: FilterOption) =>
            a.label.localeCompare(b.label)
          ),
      ],
    },
    {
      key: 'recurrence',
      label: t(
        'web.admin.workOrder.preventiveMaintenance.schedule.tableColumn.recurrence'
      ),
      type: NativeFilterTypes.Multiselect,
      options:
        filterData?.recurrences?.map((rec: string) => ({
          label: t(
            `web.admin.workOrder.preventiveMaintenance.schedule.${rec.replace(
              /-/g,
              ''
            )}`
          ),
          value: rec,
        })) || [],
    },
    {
      key: 'next_due_date',
      label: t(
        'web.admin.workOrder.preventiveMaintenance.schedule.tableColumn.nextDueDate'
      ),
      type: NativeFilterTypes.DateRange,
    },
  ];

  if (!channel?.settings?.hasWorkOrderEquipmentEnabled) {
    filters = filters.filter(column => column.key !== 'equipment');
  }

  const isLoading = loading || schedulesLoading;

  return (totalSchedules === undefined || Number(totalSchedules) === 0) &&
    !isLoading &&
    canCreateSchedule &&
    !hasFilterValue ? (
    <EmptyPageView
      icon="tools"
      title={t(
        'web.admin.workOrder.preventiveMaintenance.schedule.emptyTable.title'
      )}
      message={
        <DangerousTranslate
          translationKey="web.admin.workOrder.preventiveMaintenance.schedule.emptyTable.subtext"
          values={{}}
        />
      }
      primaryButton={{
        href: routes.channelAdminWorkOrdersPMScheduleCreate.replace(
          ':id',
          channel?.slug
        ),
        label: t`web.admin.workOrder.preventiveMaintenance.addSchedule`,
      }}
      secondaryButton={
        dataImporterFlag
          ? {
              href: routes.channelAdminDataImportNew.replace(
                ':id',
                channel?.slug
              ),
              label: t`web.admin.importer.workorders.list.newImport`,
            }
          : undefined
      }
    />
  ) : (
    <Table
      isLoading={isLoading}
      columns={columns}
      hasKeywordFilter
      showColumnVisibility
      keywordFilterLabel={t(
        'web.admin.workOrder.preventiveMaintenance.search.schedule.label'
      )}
      emptyMessage={t('web.admin.serviceRequest.emptyTable.message')}
      data={tableRows}
      totalRows={Number(searchParams.total)}
      tableKey="workOrder.scheduleTable"
      filters={filters}
      exportOptions={exportOptions}
      pagination="server"
      queryStringsEnabled
    />
  );
}

export default ScheduleList;
